"use client"

import clsx from "clsx"
import useEmblaCarousel, { UseEmblaCarouselType } from "embla-carousel-react"
import { useEffect, useMemo, useRef, useState } from "react"
import { CarouselArrow } from "src/components/generic/CarouselArrow"
import {
  npPageGapClassName,
  npPagePbClassName,
  npPagePtClassName,
  npPagePxClassName,
} from "src/components/generic/constants"
import { Icon } from "src/components/generic/Icon"
import { Img } from "src/components/generic/Img"
import { Modal } from "src/components/generic/Modal"
import { OverlayButton } from "src/components/generic/OverlayButton"
import { ResponsiveImage } from "src/components/generic/ResponsiveImage"
import { useEventCallback } from "src/components/hooks/useEventCallback"
import { ImageData } from "src/data/product-details/types"
import { Ccm19EmbeddingName } from "src/integrations/ccm19/embeddings"
import { useCcm19EmbeddingAccepted } from "src/integrations/ccm19/useConsented"
import { MissingConsentNotice } from "src/integrations/lib/MissingConsentNotice"
import { pauseYouTubeVideo, useYouTubePlaying } from "src/integrations/youtube/state"
import { YouTubeIframe } from "src/integrations/youtube/YouTubeIframe"
import { buildYtPreviewUrl, ytPreviewQualities } from "src/integrations/youtube/YouTubePreview"
import { emblaOptions, useEmbla } from "src/utilities/embla"
import { toImageCdnUrl } from "src/utilities/images"
import { ProductImageSize, toProductImageCdnUrl } from "src/utilities/productImages"
import { smoothScrollIntoView, smoothScrollToTop } from "src/utilities/scroll"

function getYouTubeVideoId(src: string): string | null {
  return src.startsWith("https://www.youtube.com/watch") ? new URL(src).searchParams.get("v") : null
}

type EmblaApi = UseEmblaCarouselType[1]

export type SlideImageFit = "contain" | "cover"

type SlideImageProps = ImageData & {
  size: ProductImageSize
}

const SlideImage = ({ src, alt, loading, size }: SlideImageProps) => {
  const videoId = getYouTubeVideoId(src)
  const youTubeConsented = useCcm19EmbeddingAccepted(Ccm19EmbeddingName.YouTube)

  return (
    <div className="flex aspect-square w-full overflow-hidden rounded-3xl bg-gray-50" style={{ maxWidth: size + "px" }}>
      {videoId ? (
        youTubeConsented ? (
          <YouTubeIframe videoId={videoId} />
        ) : (
          <div className="relative size-full">
            <Img
              src={toImageCdnUrl(buildYtPreviewUrl(videoId, ytPreviewQualities.lg))}
              alt={alt}
              className="flex size-full object-cover"
            />
            <div className="absolute inset-0 z-1 flex size-full items-center justify-center">
              <MissingConsentNotice consentId={Ccm19EmbeddingName.YouTube} />
            </div>
          </div>
        )
      ) : (
        <ResponsiveImage
          src={toProductImageCdnUrl(src, size)}
          alt={alt}
          loading={loading}
          width={size}
          height={size}
          className="embla__slide_content object-contain"
        />
      )}
    </div>
  )
}

const Slide = (props: SlideImageProps) => {
  return (
    <li className="embla__slide max-h-full min-w-0 shrink-0 grow-0 basis-full pl-4">
      <SlideImage {...props} />
    </li>
  )
}

const Slides = ({ slides, imageSize }: { slides: ImageData[]; imageSize: number }) => {
  return (
    <ul className="embla__container -ml-4 flex max-h-full">
      {slides.map((image, index) => {
        return (
          <Slide
            key={index}
            src={image.src}
            alt={image.alt || `Bild ${index + 1}`}
            loading={image.loading}
            size={imageSize}
          />
        )
      })}
    </ul>
  )
}

const Thumbnail = ({
  embla,
  slide,
  index,
  active,
}: {
  embla: EmblaApi | null
  slide: ImageData
  index: number
  active: boolean
}) => {
  const videoId = getYouTubeVideoId(slide.src)

  const onClick = useEventCallback(() => {
    embla?.scrollTo(index)
    smoothScrollToTop()
  })

  return (
    <div className="embla__slide h-full max-h-full min-w-0 shrink-0 grow-0 basis-22 pl-2 md:basis-27">
      <button
        type="button"
        onClick={onClick}
        className={clsx(
          "embla__slide_content flex aspect-square items-center justify-center overflow-hidden rounded-lg border transition-colors",
          {
            "border-np-green": active,
          },
        )}
      >
        {videoId ? (
          <div className="relative size-full">
            <Img
              src={toImageCdnUrl(buildYtPreviewUrl(videoId, ytPreviewQualities.sm))}
              alt={slide.alt}
              className="size-full rounded-lg object-cover"
            />
            <div className="absolute inset-0 z-1 flex size-full items-center justify-center">
              <Icon name="play_filled" className="size-12 text-white" />
            </div>
          </div>
        ) : (
          <Img
            src={toProductImageCdnUrl(slide.src, ProductImageSize.Tiny)}
            alt={slide.alt}
            className="size-full rounded-lg object-contain"
          />
        )}
      </button>
    </div>
  )
}

const ThumbnailsNavigation = ({
  embla,
  slides,
  activeIndex: parentIndex,
}: {
  embla: EmblaApi
  slides: ImageData[]
  activeIndex: number
}) => {
  const [miniRef, emblaMini] = useEmblaCarousel({ ...emblaOptions, slidesToScroll: 1, align: "start" })

  useEffect(() => {
    if (!emblaMini) return

    const miniIndex = emblaOptions.loop ? parentIndex : Math.max(0, parentIndex - 1)

    emblaMini.scrollTo(miniIndex)
  }, [parentIndex, emblaMini])

  return (
    <div ref={miniRef} className="embla__viewport h-21 w-full overflow-hidden rounded-lg md:h-26">
      <div className="embla__container -ml-2 flex max-h-full rounded-lg p-px">
        {slides.map((slide, index) => {
          const active = index === parentIndex

          return <Thumbnail key={index} embla={embla} slide={slide} index={index} active={active} />
        })}
      </div>
    </div>
  )
}

export const Fullscreen = ({
  slides,
  activeIndex,
  onClose,
}: {
  slides: ImageData[]
  activeIndex: number
  onClose: () => void
}) => {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const images = ref.current?.children
    const image = images?.[activeIndex]

    if (image) {
      smoothScrollIntoView(image, {
        block: "center",
        inline: "center",
        scrollMode: "always",
        behavior: "instant",
      })
    }
  }, [activeIndex])

  return (
    <Modal onClose={onClose} alwaysFullscreen>
      <div className="h-screen max-h-full w-screen overflow-x-hidden overflow-y-auto rounded-3xl">
        <div
          ref={ref}
          className={clsx(
            npPagePxClassName,
            npPagePtClassName,
            npPagePbClassName,
            npPageGapClassName,
            "mx-auto flex w-screen max-w-5xl flex-col",
          )}
        >
          {slides.map((image, index) => {
            return (
              <SlideImage
                key={index}
                src={image.src}
                alt={image.alt || `Bild ${index + 1}`}
                loading={image.loading}
                size={ProductImageSize.Huge}
              />
            )
          })}
        </div>
      </div>
    </Modal>
  )
}

export type ProductImagesProps = {
  name: string
  images: string[]
  setupVideo?: string
}

export const ProductImages = ({ name, images, setupVideo }: ProductImagesProps) => {
  const sectionRef = useRef<HTMLDivElement>(null)
  const { ref, embla, activeIndex, hasPrev, hasNext, justifyCenter } = useEmbla()
  const [fullscreen, setFullscreen] = useState(false)
  const youTubePlaying = useYouTubePlaying()

  useEffect(() => {
    activeIndex // mark as dependency
    fullscreen // mark as dependency
    pauseYouTubeVideo()
  }, [activeIndex, fullscreen])

  const onPrevClick = useEventCallback(() => embla?.scrollPrev())
  const onNextClick = useEventCallback(() => embla?.scrollNext())
  const openFullScreen = useEventCallback(() => setFullscreen(true))
  const closeFullscreen = useEventCallback(() => setFullscreen(false))

  const slides = useMemo(() => {
    const slides = images.map((image, index) => {
      return {
        src: image,
        alt: `${name} (Bild ${index + 1})`,
        loading: index === 0 ? "eager" : undefined,
      } satisfies ImageData
    })

    if (setupVideo) {
      slides.splice(3, 0, {
        src: setupVideo,
        alt: `${name} (Aufbauvideo)`,
        loading: undefined,
      } satisfies ImageData)
    }

    return slides
  }, [name, images, setupVideo])

  const activeSlideIsVideo = !!slides[activeIndex] && !!getYouTubeVideoId(slides[activeIndex].src)
  const isPlayingVideo = activeSlideIsVideo && youTubePlaying

  return (
    <div ref={sectionRef} className="flex w-full flex-col gap-4">
      <div className="embla relative flex w-full flex-col gap-4">
        <div ref={ref} className="embla__viewport overflow-hidden rounded-3xl">
          <Slides slides={slides} imageSize={ProductImageSize.Large} />
        </div>

        {hasPrev && !isPlayingVideo && (
          <div className="absolute top-1/2 left-4 -translate-y-1/2">
            <CarouselArrow side="left" onClick={onPrevClick} />
          </div>
        )}
        {hasNext && !isPlayingVideo && (
          <div className="absolute top-1/2 right-4 -translate-y-1/2">
            <CarouselArrow side="right" onClick={onNextClick} />
          </div>
        )}

        {!isPlayingVideo && (
          <div className="absolute right-0 bottom-0 m-4">
            <OverlayButton title="Vollbild" iconName="zoom_in" onClick={openFullScreen} />
          </div>
        )}
      </div>

      <ThumbnailsNavigation embla={embla} slides={slides} activeIndex={activeIndex} />

      {fullscreen && <Fullscreen slides={slides} activeIndex={activeIndex} onClose={closeFullscreen} />}
    </div>
  )
}
